/**
 * Strict validation for untrusted board files.
 *
 * Everything arriving from a file the user picked is untrusted. Rules:
 *  - Hard-reject anything structurally wrong, with a field path in the message.
 *  - REPAIR references rather than rejecting: a dangling arrow binding or a
 *    missing text container is dropped, so a hand-edited file still opens.
 *  - Never put file content in an error message or a log line. Messages name
 *    fields and expected types only.
 */

import { CONFIG } from '../config';
import { DOC_VERSION, type Board, type Point, type SlateElement } from './types';

export const FORMAT_ID = 'slate-whiteboard';
export const MAX_ELEMENTS = 10_000;
export const MAX_POINTS_PER_ELEMENT = 10_000;
export const MAX_TEXT_LENGTH = 20_000;
export const MAX_NAME_LENGTH = 200;
const MAX_ERRORS = 25;
const COORD_LIMIT = 1e7;

/** Invisible characters that break SVG output. Newline and tab are kept. */
const CONTROL_CHARS = new RegExp('[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F]', 'g');

export type ValidationResult =
  | { readonly ok: true; readonly boards: readonly Board[]; readonly repairs: readonly string[] }
  | { readonly ok: false; readonly errors: readonly string[] };

const ELEMENT_TYPES = new Set(['rect', 'ellipse', 'diamond', 'arrow', 'freedraw', 'text']);
const FILL_STYLES = new Set(['solid', 'transparent']);
const STROKE_STYLES = new Set(['solid', 'dashed', 'dotted']);
const FONT_FAMILIES = new Set(['sans', 'mono']);
const TEXT_ALIGNS = new Set(['left', 'center', 'right']);

/**
 * Colors are restricted to hex, rgb/rgba and the literal "transparent". This is
 * a real safety check, not cosmetics: colors are written straight into exported
 * SVG attributes, and a url() or javascript: scheme there would become live
 * content in whatever opens the file.
 */
const COLOR_PATTERN =
  /^(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{8}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\))$/;

export function isSafeColor(value: unknown): value is string {
  return typeof value === 'string' && (value === 'transparent' || COLOR_PATTERN.test(value));
}

class Collector {
  readonly errors: string[] = [];
  readonly repairs: string[] = [];

  fail(path: string, message: string): void {
    if (this.errors.length < MAX_ERRORS) this.errors.push(`${path}: ${message}`);
  }

  repair(message: string): void {
    if (!this.repairs.includes(message)) this.repairs.push(message);
  }

  get failed(): boolean {
    return this.errors.length > 0;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(
  value: unknown,
  path: string,
  c: Collector,
  fallback: number,
  limit = COORD_LIMIT,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    c.fail(path, 'expected a finite number');
    return fallback;
  }
  if (Math.abs(value) > limit) {
    c.repair('Out-of-range coordinates were clamped');
    return Math.sign(value) * limit;
  }
  return value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function points(value: unknown, path: string, c: Collector): Point[] {
  if (!Array.isArray(value)) {
    c.fail(path, 'expected an array of [x, y] pairs');
    return [];
  }
  if (value.length > MAX_POINTS_PER_ELEMENT) {
    c.fail(path, `has more than ${MAX_POINTS_PER_ELEMENT} points`);
    return [];
  }
  const out: Point[] = [];
  for (let i = 0; i < value.length; i++) {
    const p: unknown = value[i];
    if (!Array.isArray(p) || p.length !== 2) {
      c.fail(`${path}[${i}]`, 'expected an [x, y] pair');
      continue;
    }
    out.push([num(p[0], `${path}[${i}][0]`, c, 0), num(p[1], `${path}[${i}][1]`, c, 0)]);
  }
  return out;
}

function binding(
  value: unknown,
  path: string,
  c: Collector,
): { elementId: string; gap: number } | null {
  if (value === null || value === undefined) return null;
  if (!isObject(value)) {
    c.fail(path, 'expected an object or null');
    return null;
  }
  if (typeof value.elementId !== 'string' || value.elementId.length === 0) {
    c.fail(`${path}.elementId`, 'expected a non-empty string');
    return null;
  }
  return { elementId: value.elementId, gap: clamp(num(value.gap, `${path}.gap`, c, 4, 1000), 0, 64) };
}

function element(raw: unknown, index: number, c: Collector): SlateElement | null {
  const path = `elements[${index}]`;
  if (!isObject(raw)) {
    c.fail(path, 'expected an object');
    return null;
  }
  const type = raw.type;
  if (typeof type !== 'string' || !ELEMENT_TYPES.has(type)) {
    c.fail(`${path}.type`, `expected one of ${[...ELEMENT_TYPES].join(', ')}`);
    return null;
  }
  if (typeof raw.id !== 'string' || raw.id.length === 0 || raw.id.length > 128) {
    c.fail(`${path}.id`, 'expected a string of 1 to 128 characters');
    return null;
  }
  if (!isSafeColor(raw.strokeColor)) {
    c.fail(`${path}.strokeColor`, 'expected a hex, rgb(), rgba() or transparent color');
    return null;
  }
  if (!isSafeColor(raw.backgroundColor)) {
    c.fail(`${path}.backgroundColor`, 'expected a hex, rgb(), rgba() or transparent color');
    return null;
  }

  const common = {
    id: raw.id,
    x: num(raw.x, `${path}.x`, c, 0),
    y: num(raw.y, `${path}.y`, c, 0),
    width: Math.abs(num(raw.width, `${path}.width`, c, 0)),
    height: Math.abs(num(raw.height, `${path}.height`, c, 0)),
    angle: num(raw.angle, `${path}.angle`, c, 0, Math.PI * 4),
    strokeColor: raw.strokeColor,
    backgroundColor: raw.backgroundColor,
    fillStyle: (FILL_STYLES.has(String(raw.fillStyle)) ? raw.fillStyle : 'transparent') as
      | 'solid'
      | 'transparent',
    strokeWidth: clamp(num(raw.strokeWidth, `${path}.strokeWidth`, c, 2, 100), 0.5, 16),
    strokeStyle: (STROKE_STYLES.has(String(raw.strokeStyle)) ? raw.strokeStyle : 'solid') as
      | 'solid'
      | 'dashed'
      | 'dotted',
    opacity: clamp(num(raw.opacity, `${path}.opacity`, c, 1, 100), 0.05, 1),
    version: Math.max(1, Math.floor(num(raw.version, `${path}.version`, c, 1, 1e9))),
    updatedAt: Math.max(0, Math.floor(num(raw.updatedAt, `${path}.updatedAt`, c, 0, 1e15))),
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy.slice(0, 64) : 'imported',
    isDeleted: raw.isDeleted === true,
  };

  switch (type) {
    case 'rect':
    case 'ellipse':
    case 'diamond':
      return Object.freeze({ ...common, type }) as SlateElement;
    case 'arrow': {
      const pts = points(raw.points, `${path}.points`, c);
      if (pts.length < 2) {
        c.fail(`${path}.points`, 'an arrow needs at least two points');
        return null;
      }
      return Object.freeze({
        ...common,
        type: 'arrow' as const,
        points: pts,
        startBinding: binding(raw.startBinding, `${path}.startBinding`, c),
        endBinding: binding(raw.endBinding, `${path}.endBinding`, c),
        startArrowhead: raw.startArrowhead === true,
        endArrowhead: raw.endArrowhead !== false,
      }) as SlateElement;
    }
    case 'freedraw': {
      const pts = points(raw.points, `${path}.points`, c);
      if (pts.length < 1) {
        c.fail(`${path}.points`, 'a drawing needs at least one point');
        return null;
      }
      return Object.freeze({ ...common, type: 'freedraw' as const, points: pts }) as SlateElement;
    }
    case 'text': {
      if (typeof raw.text !== 'string') {
        c.fail(`${path}.text`, 'expected a string');
        return null;
      }
      if (raw.text.length > MAX_TEXT_LENGTH) {
        c.fail(`${path}.text`, `is longer than ${MAX_TEXT_LENGTH} characters`);
        return null;
      }
      return Object.freeze({
        ...common,
        type: 'text' as const,
        text: raw.text.replace(CONTROL_CHARS, ''),
        fontSize: clamp(num(raw.fontSize, `${path}.fontSize`, c, 16, 1000), 8, 96),
        fontFamily: (FONT_FAMILIES.has(String(raw.fontFamily)) ? raw.fontFamily : 'sans') as
          | 'sans'
          | 'mono',
        textAlign: (TEXT_ALIGNS.has(String(raw.textAlign)) ? raw.textAlign : 'left') as
          | 'left'
          | 'center'
          | 'right',
        containerId: typeof raw.containerId === 'string' ? raw.containerId : null,
      }) as SlateElement;
    }
    default:
      return null;
  }
}

/** Drop references that point nowhere. A repaired file still opens. */
function repairReferences(elements: readonly SlateElement[], c: Collector): SlateElement[] {
  const ids = new Set(elements.map((el) => el.id));
  return elements.map((el) => {
    if (el.type === 'text' && el.containerId && !ids.has(el.containerId)) {
      c.repair('Text labels with a missing container were unlinked');
      return Object.freeze({ ...el, containerId: null });
    }
    if (el.type === 'arrow') {
      const startOk = !el.startBinding || ids.has(el.startBinding.elementId);
      const endOk = !el.endBinding || ids.has(el.endBinding.elementId);
      const selfLoop =
        !!el.startBinding && !!el.endBinding && el.startBinding.elementId === el.endBinding.elementId;
      if (!startOk || !endOk || selfLoop) {
        c.repair('Arrow connections pointing at missing shapes were removed');
        return Object.freeze({
          ...el,
          startBinding: startOk && !selfLoop ? el.startBinding : null,
          endBinding: endOk && !selfLoop ? el.endBinding : null,
        });
      }
    }
    return el;
  });
}

function board(raw: unknown, index: number, c: Collector): Board | null {
  const path = index < 0 ? 'board' : `boards[${index}]`;
  if (!isObject(raw)) {
    c.fail(path, 'expected an object');
    return null;
  }
  if (typeof raw.id !== 'string' || raw.id.length === 0 || raw.id.length > 128) {
    c.fail(`${path}.id`, 'expected a string of 1 to 128 characters');
    return null;
  }
  const name =
    typeof raw.name === 'string' && raw.name.trim().length > 0
      ? raw.name.trim().slice(0, MAX_NAME_LENGTH)
      : 'Imported board';
  if (!Array.isArray(raw.elements)) {
    c.fail(`${path}.elements`, 'expected an array');
    return null;
  }
  if (raw.elements.length > MAX_ELEMENTS) {
    c.fail(`${path}.elements`, `has more than ${MAX_ELEMENTS} elements`);
    return null;
  }

  const parsed: SlateElement[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < raw.elements.length; i++) {
    const el = element(raw.elements[i], i, c);
    if (!el) continue;
    if (seen.has(el.id)) {
      c.repair('Duplicate element ids were dropped');
      continue;
    }
    seen.add(el.id);
    parsed.push(el);
  }
  if (c.failed) return null;

  const view = isObject(raw.view) ? raw.view : {};
  return {
    id: raw.id,
    name,
    createdAt: Math.max(0, Math.floor(num(raw.createdAt, `${path}.createdAt`, c, Date.now(), 1e15))),
    updatedAt: Math.max(0, Math.floor(num(raw.updatedAt, `${path}.updatedAt`, c, Date.now(), 1e15))),
    docVersion: DOC_VERSION,
    elements: repairReferences(parsed, c),
    view: {
      scrollX: typeof view.scrollX === 'number' && Number.isFinite(view.scrollX) ? view.scrollX : 0,
      scrollY: typeof view.scrollY === 'number' && Number.isFinite(view.scrollY) ? view.scrollY : 0,
      zoom: typeof view.zoom === 'number' && Number.isFinite(view.zoom) ? clamp(view.zoom, 0.1, 8) : 1,
    },
    // Imported boards are never flagged as samples: only this app seeds sample
    // data, and the badge has to stay trustworthy.
    isSample: false,
  };
}

/** Validate an already-parsed JSON value. */
export function validateBoardFile(raw: unknown): ValidationResult {
  const c = new Collector();
  if (!isObject(raw)) {
    return { ok: false, errors: ['file: expected a JSON object at the top level'] };
  }
  if (raw.format !== FORMAT_ID) {
    return {
      ok: false,
      errors: [`format: expected "${FORMAT_ID}" (this file was not exported by this app)`],
    };
  }
  if (typeof raw.version !== 'number' || !Number.isFinite(raw.version)) {
    return { ok: false, errors: ['version: expected a number'] };
  }
  if (raw.version > DOC_VERSION) {
    return {
      ok: false,
      errors: [`version: file is version ${raw.version}, this app understands up to ${DOC_VERSION}`],
    };
  }

  const boards: Board[] = [];
  if (Array.isArray(raw.boards)) {
    if (raw.boards.length === 0) return { ok: false, errors: ['boards: the file contains no boards'] };
    if (raw.boards.length > 500) {
      return { ok: false, errors: ['boards: more than 500 boards in one file'] };
    }
    for (let i = 0; i < raw.boards.length; i++) {
      const b = board(raw.boards[i], i, c);
      if (b) boards.push(b);
    }
  } else if (isObject(raw.board)) {
    const b = board(raw.board, -1, c);
    if (b) boards.push(b);
  } else {
    return { ok: false, errors: ['file: expected a "board" object or a "boards" array'] };
  }

  if (c.failed) return { ok: false, errors: c.errors };
  if (boards.length === 0) return { ok: false, errors: ['file: no readable boards found'] };
  return { ok: true, boards, repairs: c.repairs };
}

/** Parse and validate raw file text. Size is checked before parsing. */
export function parseBoardFile(text: string, maxBytes = CONFIG.maxImportBytes): ValidationResult {
  const bytes = new TextEncoder().encode(text).length;
  if (bytes > maxBytes) {
    return {
      ok: false,
      errors: [
        `file: ${(bytes / 1_048_576).toFixed(1)} MB exceeds the ${(maxBytes / 1_048_576).toFixed(
          0,
        )} MB import limit`,
      ],
    };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    // Deliberately does not echo the file contents or the parser offset.
    return { ok: false, errors: ['file: not valid JSON'] };
  }
  return validateBoardFile(parsed);
}
